import React, { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { API_URL } from "../config";
import { useAuth } from "../AuthContext";

// Экран единого входа: сайт-партнёр просит данные врача. Показывает, что именно будет передано,
// и только после «Разрешить» просит у API одноразовый код (см. backend/oidc_routes.py).
export default function OidcContinue({ onLoginRequest }) {
  const { token, loading } = useAuth();
  const requestId = new URLSearchParams(window.location.search).get("request");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const call = (path, method = "GET") =>
    fetch(`${API_URL}/oidc/request/${requestId}${path}`, { method, headers: { Authorization: `Bearer ${token}` } });

  const go = async path => {
    setBusy(true);
    const r = await call(path, "POST");
    const body = await r.json().catch(() => ({}));
    if (r.ok && body.redirect_to) { window.location.assign(body.redirect_to); return; }
    setError(body.detail || "Не удалось продолжить вход");
    setBusy(false);
  };

  useEffect(() => {
    if (loading) return;
    if (!token) { onLoginRequest?.(); return; }
    call("").then(async r => {
      const body = await r.json().catch(() => ({}));
      if (!r.ok) { setError(body.detail || "Запрос входа не найден"); return; }
      setInfo(body);
      // Согласие на эти данные уже давалось — не задерживаем врача лишним экраном
      if (body.already_consented && !body.problem) go("/approve");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, token]);

  if (!requestId) return <section className="oidc-card"><p>Не найден запрос входа. Вернитесь на сайт и нажмите «Войти через Современный врач» ещё раз.</p></section>;
  if (error) return <section className="oidc-card"><h2>Не удалось выполнить вход</h2><p role="alert">{error}</p></section>;
  if (!token && !loading) return <section className="oidc-card"><h2>Вход на «Современный врач»</h2><p>Войдите в свою учётную запись, чтобы продолжить.</p><button className="header-auth-btn" onClick={onLoginRequest}>Войти</button></section>;
  if (!info) return <section className="oidc-card"><p>Загрузка…</p></section>;
  if (info.already_consented && !info.problem) return <section className="oidc-card"><p>Продолжаем вход…</p></section>;

  return (
    <section className="oidc-card">
      <ShieldCheck size={40} strokeWidth={1.6} color="#007c84" />
      <h2>Войти на сайт «{info.client_name}»</h2>
      <p>Вы входите как <b>{info.user.name}</b> ({info.user.email}). Сайт получит:</p>
      <ul className="oidc-scopes">
        {info.scopes.map(s => <li key={s.scope}>{s.title}</li>)}
      </ul>
      {info.operator && info.operator.name && (
        <div className="oidc-operator">
          <div><b>Оператор персональных данных:</b> {info.operator.name}{info.operator.details ? `, ${info.operator.details}` : ""}</div>
          {info.operator.purpose && <div><b>Цель:</b> {info.operator.purpose}</div>}
          {info.operator.privacy_url && <div><a href={info.operator.privacy_url} target="_blank" rel="noopener noreferrer">Политика конфиденциальности сайта</a></div>}
        </div>
      )}
      <p className="oidc-note">Пароль сайту не передаётся. Согласие можно отозвать в любой момент в профиле.</p>
      {info.problem && <p className="oidc-problem" role="alert">{info.problem}</p>}
      <div className="oidc-actions">
        <button className="header-auth-btn" disabled={busy || !!info.problem} onClick={() => go("/approve")}>Разрешить и войти</button>
        <button className="oidc-cancel" disabled={busy} onClick={() => go("/deny")}>Отмена</button>
      </div>
    </section>
  );
}
